import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Modal } from "../../../components/ui/Modal";
import { useCreateBusiness } from "../../../hooks/useBusinesses";
import { ApiError } from "../../../lib/api";

const schema = z.object({
  name: z.string().min(1, "Business name is required").max(100),
  address: z.string().min(1, "Address is required").max(200),
});

type FormValues = z.infer<typeof schema>;

const INPUT_CLASS =
  "w-full rounded-lg border border-[#e2e8f0] bg-[#f8fafc] px-3 py-2 text-sm text-[#0f172a] placeholder:text-[#94a3b8] focus:bg-white focus:border-[#2447d9] focus:outline-none focus:ring-2 focus:ring-[#2447d9]/20";
const LABEL_CLASS = "block text-xs font-medium text-[#374151] mb-1";
const ERROR_CLASS = "text-[#dc2626] text-xs mt-1";

interface AddBusinessModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function AddBusinessModal({ isOpen, onClose, onSuccess }: AddBusinessModalProps) {
  const createBusiness = useCreateBusiness();

  const {
    register,
    handleSubmit,
    setError,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  const onSubmit = async (data: FormValues) => {
    try {
      await createBusiness.mutateAsync(data);
      reset();
      onSuccess();
    } catch (err) {
      if (err instanceof ApiError) {
        setError("root", { message: err.message });
      } else {
        setError("root", { message: "Something went wrong. Please try again." });
      }
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Add New Business" size="md">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <label className={LABEL_CLASS}>Business Name</label>
          <input
            {...register("name")}
            className={INPUT_CLASS}
            placeholder="e.g. Medel Makati Branch"
            autoFocus
          />
          {errors.name && <p className={ERROR_CLASS}>{errors.name.message}</p>}
        </div>

        <div>
          <label className={LABEL_CLASS}>Address</label>
          <textarea
            {...register("address")}
            className={`${INPUT_CLASS} resize-none`}
            rows={3}
            placeholder="123 Ayala Ave, Makati City"
          />
          {errors.address && <p className={ERROR_CLASS}>{errors.address.message}</p>}
        </div>

        {errors.root && (
          <p className="rounded-lg bg-[#fef2f2] px-3 py-2 text-sm text-[#dc2626]">
            {errors.root.message}
          </p>
        )}

        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className="rounded-lg border border-[#e2e8f0] px-4 py-2 text-sm font-medium text-[#374151] hover:bg-[#f8fafc] transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="rounded-lg bg-[#2447d9] px-4 py-2 text-sm font-medium text-white hover:bg-[#1e3a8a] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isSubmitting ? "Creating…" : "Create Business"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
